/**
 * Add Object panel — Blender-style popover with category submenus.
 *
 * Browse mode:  Category list (Mesh, Environments, Props, Light, Mannequin,
 *               Library, Import).
 *               Hover or press Enter / → on a category to open its flyout submenu.
 * Search mode:  Typing filters all categories in real time and shows a flat
 *               result list annotated with category labels.
 *
 * Keyboard — browse:
 *   ↑ ↓          Navigate categories
 *   Enter / →    Open submenu for active category
 *   Esc          Close panel
 *
 * Keyboard — submenu open:
 *   ↑ ↓          Navigate submenu items
 *   Enter        Activate item
 *   ← / Esc      Close submenu, return to category list
 *
 * Keyboard — search:
 *   ↑ ↓          Navigate results
 *   Enter        Activate result
 *   Esc          Close panel
 */

import type { FolderResponse, LibraryItemResponse } from "@poseref/shared";
import {
  Archive,
  Armchair,
  BedDouble,
  Box,
  Building2,
  Cable,
  ChevronRight,
  Circle,
  CircleDot,
  Crosshair,
  Cylinder,
  Disc,
  Donut,
  FileBox,
  FileUp,
  Folder,
  Footprints,
  HardHat,
  House,
  Image as ImageIcon,
  LampFloor,
  Layers,
  Library,
  Lightbulb,
  Soup,
  Spline,
  Square,
  Sun,
  Table,
  Triangle,
  Type,
  User,
} from "lucide-react";
import type { RefObject } from "react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useIsTrial } from "../app/authStore.ts";
import {
  importBlendAndLoad,
  importMeshFileAndSpawn,
} from "../app/importToLibrary.ts";
import { useLibraryStore } from "../app/libraryStore.ts";
import {
  MESH_IMPORT_ACCEPT,
  MESH_IMPORT_DISPLAY,
} from "../app/meshImportFormats.ts";
import { compressAndStoreReferenceImage } from "../app/referenceImageUpload.ts";
import { spawnFromCloudLibrary } from "../app/spawnFromLibrary.ts";
import { useStore } from "../app/store.ts";
import {
  AVAILABLE_BODY_TYPES,
  MANNEQUIN_MODELS,
} from "../mannequin/registry.ts";
import { GENERATORS } from "../modeling/generators/registry.ts";
import type { PrimitiveKind } from "../modeling/types.ts";
import { getTopPanelZ } from "./DraggablePanel.tsx";
import { MenuButtonRow } from "./menu/MenuPrimitives.tsx";
import { Popover } from "./Popover.tsx";
import { SearchField } from "./shared.tsx";
import { GLASS_PANEL } from "./styles.ts";
import {
  COLOR_ACCENT,
  COLOR_CAUTION,
  COLOR_GOLD,
  COLOR_MOD_SUBSURF,
  COLOR_TEXT_DIM,
  COLOR_TEXT_FAINT,
  COLOR_TYPE_BLEND,
  FONT_SIZE_SMALL,
  FONT_SIZE_TINY,
  Z_POPOVER,
} from "./tokens.ts";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CategoryId =
  | "mesh"
  | "environments"
  | "props"
  | "light"
  | "mannequin"
  | "image_plane"
  | "library"
  | "import";

type TopLevelItem =
  | { kind: "submenu"; id: CategoryId; label: string; icon: React.ReactNode }
  | {
      kind: "generator";
      id: string;
      generatorId: string;
      label: string;
      icon: React.ReactNode;
    };

// ---------------------------------------------------------------------------
// Static data
// ---------------------------------------------------------------------------

const MESH_ITEMS: {
  kind: PrimitiveKind;
  label: string;
  icon: React.ReactNode;
}[] = [
  { kind: "cube", label: "Cube", icon: <Box size={15} /> },
  { kind: "circle", label: "Circle", icon: <Disc size={15} /> },
  { kind: "cone", label: "Cone", icon: <Triangle size={15} /> },
  { kind: "sphere", label: "Sphere", icon: <Circle size={15} /> },
  { kind: "icosphere", label: "Icosphere", icon: <CircleDot size={15} /> },
  { kind: "plane", label: "Plane", icon: <Square size={15} /> },
  { kind: "torus", label: "Torus", icon: <Donut size={15} /> },
  { kind: "curve", label: "Curve", icon: <Spline size={15} /> },
  { kind: "cylinder", label: "Cylinder", icon: <Cylinder size={15} /> },
  { kind: "skin_armature", label: "Skin Armature", icon: <Cable size={15} /> },
  { kind: "empty", label: "Empty", icon: <Crosshair size={15} /> },
];

// Icon map for every generator by id. Add an entry here when registering a
// new generator so it surfaces correctly in the Environments / Props submenus
// and in search results.
const GENERATOR_ICON_BY_ID: Record<string, React.ReactNode> = {
  // Primitives (shown in Mesh submenu via addPrimitive, not here)
  cube: <Box size={15} />,
  plane: <Square size={15} />,
  circle: <Disc size={15} />,
  sphere: <Circle size={15} />,
  icosphere: <CircleDot size={15} />,
  cylinder: <Cylinder size={15} />,
  cone: <Triangle size={15} />,
  torus: <Donut size={15} />,
  // Environment generators
  room: <House size={15} />,
  // Prop generators
  chair: <Armchair size={15} />,
  cable: <Cable size={15} />,
  stairs: <Footprints size={15} />,
  table: <Table size={15} />,
  bookshelf: <Library size={15} />,
  lamp: <LampFloor size={15} />,
  bed: <BedDouble size={15} />,
  ball: <Circle size={15} />,
  bowl: <Soup size={15} />,
  chest_of_drawers: <Archive size={15} />,
  hat: <HardHat size={15} />,
  baseball_bat: <Cylinder size={15} />,
  text: <Type size={15} />,
};

// Derived lists for the Environments and Props submenus — driven entirely by
// the `category` field on each generator so adding a new generator only
// requires an icon entry above and a registry entry.
const ENVIRONMENT_GENERATORS = Object.values(GENERATORS)
  .filter((g) => g.category === "environment")
  .map((g) => ({
    id: g.id,
    label: g.label,
    icon: GENERATOR_ICON_BY_ID[g.id] ?? <Box size={15} />,
  }));

const TOP_LEVEL_GENERATOR_IDS = new Set(["text"]);

const TOP_LEVEL_GENERATORS = Object.values(GENERATORS)
  .filter((g) => TOP_LEVEL_GENERATOR_IDS.has(g.id))
  .map((g) => ({
    id: g.id,
    label: g.label,
    icon: GENERATOR_ICON_BY_ID[g.id] ?? <Box size={15} />,
  }));

const PROP_GENERATORS = Object.values(GENERATORS)
  .filter((g) => g.category === "prop" && !TOP_LEVEL_GENERATOR_IDS.has(g.id))
  .map((g) => ({
    id: g.id,
    label: g.label,
    icon: GENERATOR_ICON_BY_ID[g.id] ?? <Box size={15} />,
  }));

const LIGHT_ITEMS: {
  kind: "point" | "directional";
  label: string;
  icon: React.ReactNode;
}[] = [
  { kind: "point", label: "Point Light", icon: <Lightbulb size={15} /> },
  { kind: "directional", label: "Directional Light", icon: <Sun size={15} /> },
];

const MANNEQUIN_ITEMS: { id: string; label: string; icon: React.ReactNode }[] =
  AVAILABLE_BODY_TYPES.map((bt) => ({
    id: bt,
    label: MANNEQUIN_MODELS[bt]?.name ?? bt,
    icon: <User size={15} />,
  }));

const TOP_LEVEL_ITEMS: TopLevelItem[] = [
  { kind: "submenu", id: "mesh", label: "Mesh", icon: <Box size={15} /> },
  {
    kind: "submenu",
    id: "environments",
    label: "Environments",
    icon: <Building2 size={15} />,
  },
  { kind: "submenu", id: "props", label: "Props", icon: <Armchair size={15} /> },
  { kind: "submenu", id: "light", label: "Light", icon: <Sun size={15} /> },
  {
    kind: "submenu",
    id: "mannequin",
    label: "Mannequin",
    icon: <User size={15} />,
  },
  ...TOP_LEVEL_GENERATORS.map(
    (item): TopLevelItem => ({
      kind: "generator",
      id: item.id,
      generatorId: item.id,
      label: item.label,
      icon: item.icon,
    }),
  ),
  {
    kind: "submenu",
    id: "image_plane",
    label: "Image Plane",
    icon: <ImageIcon size={15} />,
  },
  { kind: "submenu", id: "library", label: "Library", icon: <Layers size={15} /> },
  { kind: "submenu", id: "import", label: "Import", icon: <FileUp size={15} /> },
];

const TYPE_BADGES: Record<string, { label: string; color: string }> = {
  object: { label: "obj", color: COLOR_MOD_SUBSURF },
  collection: { label: "col", color: COLOR_CAUTION },
  blend: { label: "blend", color: COLOR_TYPE_BLEND },
};

// Horizontal gap between the main panel right edge and the submenu.
const SUBMENU_GAP = 4;
// Viewport margin — minimum distance between any panel edge and the screen edge.
const VIEWPORT_MARGIN = 8;
// Delay before closing the submenu when the pointer leaves both panels.
const CLOSE_DELAY_MS = 150;
// Sentinel value: submenu renders here while its size is being measured, then
// repositioned correctly before the browser paints. Mirrors the pattern used
// by Popover.tsx so the user never sees the off-screen flash.
const OFFSCREEN = { top: -9999, left: -9999 } as const;

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function AddObjectPanel({
  anchorRef,
  open,
  onClose,
}: {
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
}) {
  // ── State ──────────────────────────────────────────────────────────────────

  const [search, setSearch] = useState("");
  // Which top-level row is currently highlighted (keyboard / hover)
  const [activeCatIdx, setActiveCatIdx] = useState(0);
  // Which category's submenu is open
  const [activeCategory, setActiveCategory] = useState<CategoryId | null>(null);
  // Portal position for the submenu
  const [submenuPos, setSubmenuPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  // Active item index within the open submenu
  const [submenuActiveIdx, setSubmenuActiveIdx] = useState(0);
  // Active item index within search results
  const [searchActiveIdx, setSearchActiveIdx] = useState(0);

  const submenuOpen = activeCategory !== null && submenuPos !== null;

  // ── Refs ───────────────────────────────────────────────────────────────────

  const mainPanelRef = useRef<HTMLDivElement>(null);
  const submenuRef = useRef<HTMLDivElement>(null);
  // Snapshotted at the moment a submenu opens so the layout-effect can read
  // stable rects even if the DOM has shifted by the time the effect fires.
  const submenuAnchorRef = useRef<{
    panelRect: DOMRect;
    rowRect: DOMRect;
  } | null>(null);
  const catRowRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const closeTimerRef = useRef<number | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const blendInputRef = useRef<HTMLInputElement>(null);
  // Ref to the main Popover's root element — used to read its captured z-index
  // so the submenu flyout can stack above it regardless of popoverOpenSequence.
  const mainPopoverRef = useRef<HTMLDivElement | null>(null);
  // Tracks whether the submenu is currently scrolling so pointer-enter events
  // don't fight the scroll and the close timer isn't re-armed mid-scroll.
  const submenuScrollingRef = useRef(false);
  const submenuScrollTimerRef = useRef<number | null>(null);

  // ── Store ──────────────────────────────────────────────────────────────────

  const anchorPoint = useStore((s) => s.operatorAnchorPoint);
  const setAnchorPoint = useStore((s) => s.setOperatorAnchorPoint);
  const focusSearch = useStore((s) => s.addObjectFocusSearch);
  const setFocusSearch = useStore((s) => s.setAddObjectFocusSearch);
  const addPrimitive = useStore((s) => s.addPrimitive);
  const addGenerator = useStore((s) => s.addGenerator);
  const addLight = useStore((s) => s.addLight);
  const addMannequin = useStore((s) => s.addMannequin);
  const addImagePlane = useStore((s) => s.addImagePlane);
  const referenceLayers = useStore((s) => s.referenceLayers);
  const isTrial = useIsTrial();

  const libraryItems = useLibraryStore((s) => s.items);
  const libraryFolders = useLibraryStore((s) => s.folders);
  const fetchAll = useLibraryStore((s) => s.fetchAll);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    new Set(),
  );

  // ── Reset on open ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setActiveCatIdx(0);
    setActiveCategory(null);
    setSubmenuPos(null);
    setSubmenuActiveIdx(0);
    setSearchActiveIdx(0);
    fetchAll();
    if (focusSearch) {
      setFocusSearch(false);
      requestAnimationFrame(() => {
        searchInputRef.current?.focus();
      });
    }
  }, [open, fetchAll, focusSearch, setFocusSearch]);

  // ── Close handler ──────────────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    setAnchorPoint(null);
    onClose();
  }, [onClose, setAnchorPoint]);

  // ── Submenu hover management ───────────────────────────────────────────────

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => {
      setActiveCategory(null);
      setSubmenuPos(null);
    }, CLOSE_DELAY_MS);
  }, [clearCloseTimer]);

  const openSubmenuForRow = useCallback(
    (catIdx: number, rowEl: HTMLElement) => {
      clearCloseTimer();
      const mainPanel = mainPanelRef.current;
      const item = TOP_LEVEL_ITEMS[catIdx];
      if (!mainPanel || !item || item.kind !== "submenu") return;

      // Snapshot layout data synchronously before React re-renders anything.
      submenuAnchorRef.current = {
        panelRect: mainPanel.getBoundingClientRect(),
        rowRect: rowEl.getBoundingClientRect(),
      };

      setActiveCatIdx(catIdx);
      setActiveCategory(item.id);
      setSubmenuActiveIdx(0);
      // Render off-screen first; the layout-effect will measure and reposition
      // before the browser paints, matching the Popover.tsx sentinel pattern.
      setSubmenuPos(OFFSCREEN);
    },
    [clearCloseTimer],
  );

  // ── Action handlers ────────────────────────────────────────────────────────

  const handleActivatePrimitive = useCallback(
    (kind: PrimitiveKind) => {
      addPrimitive(kind);
      handleClose();
    },
    [addPrimitive, handleClose],
  );

  const handleActivateGenerator = useCallback(
    (generatorId: string) => {
      addGenerator(generatorId);
      handleClose();
    },
    [addGenerator, handleClose],
  );

  const handleActivateLight = useCallback(
    (kind: "point" | "directional") => {
      addLight(kind);
      handleClose();
    },
    [addLight, handleClose],
  );

  const handleActivateMannequin = useCallback(
    (id: string) => {
      addMannequin(id);
      handleClose();
    },
    [addMannequin, handleClose],
  );

  const handleActivateLibraryItem = useCallback(
    (item: LibraryItemResponse) => {
      spawnFromCloudLibrary(item).catch((e) =>
        console.error("Failed to spawn:", e),
      );
      handleClose();
    },
    [handleClose],
  );

  const handleMeshFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = "";
      handleClose();
      await importMeshFileAndSpawn(file);
    },
    [handleClose],
  );

  // Resolve the original image's intrinsic dimensions from a blob URL. We
  // need the source pixel size (not the WebP-compressed size) so the
  // spawned plane's aspect ratio matches what the user uploaded.
  const measureImage = useCallback(
    (url: string): Promise<{ w: number; h: number }> =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () =>
          resolve({ w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = reject;
        img.src = url;
      }),
    [],
  );

  const closeSubmenu = useCallback(() => {
    clearCloseTimer();
    setActiveCategory(null);
    setSubmenuPos(null);
  }, [clearCloseTimer]);

  const imagePlaneFileInputRef = useRef<HTMLInputElement>(null);

  const handleImagePlaneFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = "";
      handleClose();
      try {
        const baseName = file.name.replace(/\.[^.]+$/, "");
        const { imageKey, imageUrl } = await compressAndStoreReferenceImage(
          file,
          baseName,
        );
        const { w, h } = await measureImage(imageUrl);
        addImagePlane(imageKey, w, h, baseName);
      } catch (err) {
        console.error("Failed to add image plane from file:", err);
      }
    },
    [addImagePlane, handleClose, measureImage],
  );

  const handleActivateReferenceImage = useCallback(
    async (imageKey: string, name: string, imageUrl: string) => {
      handleClose();
      try {
        const { w, h } = await measureImage(imageUrl);
        addImagePlane(imageKey, w, h, name);
      } catch (err) {
        console.error("Failed to add image plane from reference panel:", err);
      }
    },
    [addImagePlane, handleClose, measureImage],
  );

  const handleBlendChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = "";
      handleClose();
      await importBlendAndLoad(file, "add");
    },
    [handleClose],
  );

  // ── Search results ─────────────────────────────────────────────────────────

  type SearchResult = {
    key: string;
    categoryLabel?: string;
    label: string;
    icon: React.ReactNode;
    locked?: boolean;
    onActivate: () => void;
  };

  const q = search.toLowerCase().trim();

  const searchResults = useMemo((): SearchResult[] | null => {
    if (!q) return null;
    const results: SearchResult[] = [];

    for (const item of MESH_ITEMS) {
      if (item.label.toLowerCase().includes(q))
        results.push({
          key: `mesh-${item.kind}`,
          categoryLabel: "Mesh",
          label: item.label,
          icon: item.icon,
          onActivate: () => handleActivatePrimitive(item.kind),
        });
    }
    for (const item of ENVIRONMENT_GENERATORS) {
      if (item.label.toLowerCase().includes(q))
        results.push({
          key: `env-${item.id}`,
          categoryLabel: "Environments",
          label: item.label,
          icon: item.icon,
          onActivate: () => handleActivateGenerator(item.id),
        });
    }
    for (const item of TOP_LEVEL_GENERATORS) {
      if (item.label.toLowerCase().includes(q))
        results.push({
          key: `top-gen-${item.id}`,
          label: item.label,
          icon: item.icon,
          onActivate: () => handleActivateGenerator(item.id),
        });
    }
    for (const item of PROP_GENERATORS) {
      if (item.label.toLowerCase().includes(q))
        results.push({
          key: `prop-gen-${item.id}`,
          categoryLabel: "Props",
          label: item.label,
          icon: item.icon,
          onActivate: () => handleActivateGenerator(item.id),
        });
    }
    for (const item of LIGHT_ITEMS) {
      if (item.label.toLowerCase().includes(q))
        results.push({
          key: `light-${item.kind}`,
          categoryLabel: "Light",
          label: item.label,
          icon: item.icon,
          onActivate: () => handleActivateLight(item.kind),
        });
    }
    for (const item of MANNEQUIN_ITEMS) {
      if (item.label.toLowerCase().includes(q)) {
        const locked = isTrial && item.id !== "male-neutral";
        results.push({
          key: `mannequin-${item.id}`,
          categoryLabel: "Mannequin",
          label: item.label,
          icon: item.icon,
          locked,
          onActivate: locked
            ? () => {}
            : () => handleActivateMannequin(item.id),
        });
      }
    }
    const libObjectItems = libraryItems.filter(
      (i) =>
        i.type === "object" || i.type === "collection" || i.type === "blend",
    );
    for (const item of libObjectItems) {
      if (item.name.toLowerCase().includes(q))
        results.push({
          key: `lib-${item.id}`,
          categoryLabel: "Library",
          label: item.name,
          icon: <Box size={15} />,
          onActivate: () => handleActivateLibraryItem(item),
        });
    }
    if (
      "import mesh".includes(q) ||
      "mesh".includes(q) ||
      "obj".includes(q) ||
      "glb".includes(q) ||
      "gltf".includes(q) ||
      "fbx".includes(q)
    ) {
      results.push({
        key: "import-mesh-file",
        categoryLabel: "Import",
        label: `Import Mesh File (${MESH_IMPORT_DISPLAY})…`,
        icon: <FileUp size={15} />,
        onActivate: () => fileInputRef.current?.click(),
      });
    }
    if (
      "image".includes(q) ||
      "image plane".includes(q) ||
      "reference".includes(q) ||
      "photo".includes(q) ||
      "picture".includes(q)
    ) {
      results.push({
        key: "image-plane-from-file",
        categoryLabel: "Image Plane",
        label: "Image Plane from File…",
        icon: <ImageIcon size={15} />,
        onActivate: () => imagePlaneFileInputRef.current?.click(),
      });
    }
    for (const layer of referenceLayers) {
      if (!layer.imageUrl) continue;
      if (layer.name.toLowerCase().includes(q)) {
        results.push({
          key: `image-plane-ref-${layer.id}`,
          categoryLabel: "Image Plane",
          label: layer.name,
          icon: <ImageIcon size={15} />,
          onActivate: () =>
            handleActivateReferenceImage(
              layer.imageKey,
              layer.name,
              layer.imageUrl,
            ),
        });
      }
    }
    if ("blend".includes(q) || "import blend".includes(q))
      results.push({
        key: "import-blend",
        categoryLabel: "Import",
        label: "Add Blend File…",
        icon: <FileUp size={15} />,
        onActivate: () => blendInputRef.current?.click(),
      });

    return results;
  }, [
    q,
    isTrial,
    libraryItems,
    referenceLayers,
    handleActivatePrimitive,
    handleActivateGenerator,
    handleActivateLight,
    handleActivateMannequin,
    handleActivateLibraryItem,
    handleActivateReferenceImage,
  ]);

  // ── Keyboard navigation ────────────────────────────────────────────────────

  const getSubmenuItems = useCallback(
    () =>
      submenuRef.current
        ? Array.from(
            submenuRef.current.querySelectorAll<HTMLElement>(
              "[data-submenu-item]:not([disabled])",
            ),
          )
        : [],
    [],
  );

  useEffect(() => {
    if (!open) return;

    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey) return;

      // ── Search mode ──
      if (q) {
        const count = searchResults?.length ?? 0;
        switch (e.key) {
          case "ArrowDown":
            e.preventDefault();
            e.stopPropagation();
            setSearchActiveIdx((p) => Math.min(p + 1, count - 1));
            return;
          case "ArrowUp":
            e.preventDefault();
            e.stopPropagation();
            setSearchActiveIdx((p) => Math.max(p - 1, 0));
            return;
          case "Enter":
            e.preventDefault();
            e.stopPropagation();
            if (searchResults?.[searchActiveIdx])
              searchResults[searchActiveIdx].onActivate();
            return;
          case "Escape":
            e.preventDefault();
            e.stopPropagation();
            handleClose();
            return;
          default:
            e.stopPropagation();
            return;
        }
      }

      // ── Submenu open ──
      if (submenuOpen) {
        const items = getSubmenuItems();
        const count = items.length;
        switch (e.key) {
          case "ArrowDown":
            e.preventDefault();
            e.stopPropagation();
            setSubmenuActiveIdx((p) => Math.min(p + 1, count - 1));
            return;
          case "ArrowUp":
            e.preventDefault();
            e.stopPropagation();
            setSubmenuActiveIdx((p) => Math.max(p - 1, 0));
            return;
          case "Enter":
            e.preventDefault();
            e.stopPropagation();
            items[submenuActiveIdx]?.click();
            return;
          case "ArrowLeft":
          case "Escape":
            e.preventDefault();
            e.stopPropagation();
            setActiveCategory(null);
            setSubmenuPos(null);
            return;
          default:
            e.stopPropagation();
            return;
        }
      }

      // ── Category list ──
      const count = TOP_LEVEL_ITEMS.length;
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          e.stopPropagation();
          setActiveCatIdx((p) => Math.min(p + 1, count - 1));
          return;
        case "ArrowUp":
          e.preventDefault();
          e.stopPropagation();
          setActiveCatIdx((p) => Math.max(p - 1, 0));
          return;
        case "Enter": {
          e.preventDefault();
          e.stopPropagation();
          const item = TOP_LEVEL_ITEMS[activeCatIdx];
          if (!item) return;
          if (item.kind === "generator") {
            handleActivateGenerator(item.generatorId);
            return;
          }
          const rowEl = catRowRefs.current[activeCatIdx];
          if (rowEl) openSubmenuForRow(activeCatIdx, rowEl);
          return;
        }
        case "ArrowRight": {
          e.preventDefault();
          e.stopPropagation();
          const item = TOP_LEVEL_ITEMS[activeCatIdx];
          if (!item || item.kind !== "submenu") return;
          const rowEl = catRowRefs.current[activeCatIdx];
          if (rowEl) openSubmenuForRow(activeCatIdx, rowEl);
          return;
        }
        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          handleClose();
          return;
        default:
          e.stopPropagation();
      }
    };

    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [
    open,
    q,
    submenuOpen,
    searchResults,
    searchActiveIdx,
    submenuActiveIdx,
    activeCatIdx,
    getSubmenuItems,
    openSubmenuForRow,
    handleActivateGenerator,
    handleClose,
  ]);

  // Sync submenu item highlights + scroll into view after every render.
  // scrollIntoView is suppressed while the user is scrolling (same guard
  // as Popover.tsx) so keyboard / hover-driven focus changes don't fight
  // a user-initiated scroll gesture.
  useEffect(() => {
    if (!submenuOpen || !submenuRef.current) return;
    const items = getSubmenuItems();
    items.forEach((el, i) =>
      el.setAttribute("data-popover-active", String(i === submenuActiveIdx)),
    );
    if (!submenuScrollingRef.current) {
      items[submenuActiveIdx]?.scrollIntoView({ block: "nearest" });
    }
  });

  // Bind pointer-enter on submenu items to track mouse position.
  // Suppressed while the submenu is scrolling so items drifting under the
  // pointer mid-scroll don't fight the scroll via the scrollIntoView sync.
  useEffect(() => {
    if (!submenuOpen || !submenuRef.current) return;
    const items = getSubmenuItems();
    const cleanups = items.map((el, i) => {
      const h = () => {
        if (!submenuScrollingRef.current) setSubmenuActiveIdx(i);
      };
      el.addEventListener("pointerenter", h);
      return () => el.removeEventListener("pointerenter", h);
    });
    return () => cleanups.forEach((fn) => fn());
  }, [submenuOpen, getSubmenuItems]);

  // Track submenu scroll state so we can suppress pointer-enter noise and
  // prevent the close timer from firing while the user is scrolling.
  useEffect(() => {
    if (!submenuOpen || !submenuRef.current) return;
    const el = submenuRef.current;
    const onScroll = () => {
      submenuScrollingRef.current = true;
      clearCloseTimer();
      if (submenuScrollTimerRef.current !== null)
        clearTimeout(submenuScrollTimerRef.current);
      submenuScrollTimerRef.current = window.setTimeout(() => {
        submenuScrollingRef.current = false;
        submenuScrollTimerRef.current = null;
      }, 300);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (submenuScrollTimerRef.current !== null) {
        clearTimeout(submenuScrollTimerRef.current);
        submenuScrollTimerRef.current = null;
      }
      submenuScrollingRef.current = false;
    };
  }, [submenuOpen, clearCloseTimer]);

  // Measure the off-screen submenu and position it correctly before render.
  // Fires only when submenuPos is the OFFSCREEN sentinel, so there is no loop:
  // the setSubmenuPos call inside sets a real coordinate, the sentinel check
  // fails on the next run, and the effect exits immediately.
  useLayoutEffect(() => {
    if (!submenuPos || submenuPos.top !== OFFSCREEN.top) return;
    const el = submenuRef.current;
    const anchor = submenuAnchorRef.current;
    if (!el || !anchor) return;

    const { panelRect, rowRect } = anchor;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const M = VIEWPORT_MARGIN;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Horizontal: prefer right of the main panel, flip left if it would clip.
    let left = panelRect.right + SUBMENU_GAP;
    if (left + w > vw - M) left = Math.max(M, panelRect.left - w - SUBMENU_GAP);

    // Vertical: align to the hovered row, push up if it would clip the bottom.
    let top = rowRect.top - 4;
    if (top + h > vh - M) top = Math.max(M, vh - M - h);
    top = Math.max(M, top);

    setSubmenuPos({ top, left });
  }, [submenuPos]);

  // ── Derived display data ───────────────────────────────────────────────────

  const libDisplayItems = useMemo(
    () =>
      libraryItems.filter(
        (i) =>
          i.type === "object" || i.type === "collection" || i.type === "blend",
      ),
    [libraryItems],
  );

  const toggleFolder = useCallback((id: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // ── Popover positioning ────────────────────────────────────────────────────

  const popoverProps = anchorPoint
    ? { anchorPoint, open, onClose: handleClose }
    : { anchorRef, open, onClose: handleClose };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <Popover
        {...popoverProps}
        style={{ minWidth: 224, maxWidth: 224 }}
        rootRef={mainPopoverRef}
      >
        {/* Search field */}
        <div style={{ padding: "8px 6px 4px" }}>
          <SearchField
            value={search}
            onChange={(v) => {
              setSearch(v);
              setSearchActiveIdx(0);
            }}
            placeholder="Search objects…"
            inputRef={searchInputRef}
            clearable
          />
        </div>

        {/* Content */}
        <div style={{ paddingBottom: 4 }}>
          {searchResults !== null ? (
            // ── Search results ──────────────────────────────────────────────
            searchResults.length === 0 ? (
              <div
                style={{
                  padding: "12px 10px",
                  textAlign: "center",
                  color: COLOR_TEXT_FAINT,
                  fontSize: FONT_SIZE_SMALL,
                }}
              >
                No matches
              </div>
            ) : (
              <div
                style={{
                  maxHeight: 400,
                  overflowY: "auto",
                  overscrollBehavior: "contain",
                }}
              >
                {searchResults.map((result, i) => (
                  <MenuButtonRow
                    key={result.key}
                    disabled={result.locked}
                    onClick={result.locked ? undefined : result.onActivate}
                    active={i === searchActiveIdx}
                    style={result.locked ? { opacity: 0.45 } : undefined}
                    onPointerEnter={() => setSearchActiveIdx(i)}
                  >
                    <span
                      style={{
                        color: COLOR_TEXT_DIM,
                        display: "flex",
                        flexShrink: 0,
                      }}
                    >
                      {result.icon}
                    </span>
                    <span style={{ flex: 1 }}>{result.label}</span>
                    {result.categoryLabel && (
                      <span
                        style={{
                          fontSize: FONT_SIZE_TINY,
                          color: COLOR_TEXT_FAINT,
                          flexShrink: 0,
                        }}
                      >
                        {result.categoryLabel}
                      </span>
                    )}
                    {result.locked && (
                      <span
                        style={{
                          fontSize: FONT_SIZE_TINY,
                          color: COLOR_GOLD,
                          fontWeight: 600,
                          marginLeft: 4,
                        }}
                      >
                        Pro
                      </span>
                    )}
                  </MenuButtonRow>
                ))}
              </div>
            )
          ) : (
            // ── Category list ───────────────────────────────────────────────
            <div
              ref={mainPanelRef}
              onPointerLeave={(e) => {
                if (e.pointerType !== "touch") scheduleClose();
              }}
            >
              {TOP_LEVEL_ITEMS.map((item, idx) => (
                <MenuButtonRow
                  key={item.id}
                  ref={(el) => {
                    catRowRefs.current[idx] = el;
                  }}
                  data-category={item.id}
                  onPointerEnter={(e) => {
                    // Touch fires pointerenter before click; let onClick be the
                    // sole opener for touch so the submenu doesn't flash open
                    // and immediately close when the click arrives.
                    if (e.pointerType === "touch") return;
                    setActiveCatIdx(idx);
                    if (item.kind === "submenu") {
                      openSubmenuForRow(idx, e.currentTarget);
                    } else {
                      closeSubmenu();
                    }
                  }}
                  onClick={() => {
                    if (item.kind === "generator") {
                      handleActivateGenerator(item.generatorId);
                      return;
                    }
                    if (activeCategory === item.id) {
                      closeSubmenu();
                    } else {
                      const el = catRowRefs.current[idx];
                      if (el) openSubmenuForRow(idx, el);
                    }
                  }}
                  active={idx === activeCatIdx}
                >
                  <span
                    style={{
                      color: COLOR_TEXT_DIM,
                      display: "flex",
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </span>
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {item.kind === "submenu" && (
                    <ChevronRight
                      size={13}
                      style={{ color: COLOR_TEXT_FAINT, flexShrink: 0 }}
                    />
                  )}
                </MenuButtonRow>
              ))}
            </div>
          )}
        </div>
      </Popover>

      {/* Submenu flyout */}
      {submenuOpen &&
        open &&
        createPortal(
          <div
            ref={submenuRef}
            onPointerEnter={clearCloseTimer}
            onPointerLeave={(e) => {
              // Touch pointerleave fires when a scroll gesture ends (finger lift)
              // or when the touch point exits bounds mid-scroll.  We rely on
              // click-outside (via the main Popover's handler) to close in those
              // cases instead.
              if (e.pointerType !== "touch" && !submenuScrollingRef.current)
                scheduleClose();
            }}
            data-floating-portal
            style={{
              position: "fixed",
              top: submenuPos!.top,
              left: submenuPos!.left,
              // Always stack above the main Popover.  Read the Popover root's
              // inline z-index (set by its captured-sequence logic) so this
              // submenu lands on top regardless of how many popovers are open.
              zIndex:
                (parseInt(mainPopoverRef.current?.style.zIndex ?? "0", 10) ||
                  Z_POPOVER + getTopPanelZ()) + 1,
              ...GLASS_PANEL,
              padding: "4px",
              display: "flex",
              flexDirection: "column",
              minWidth: 220,
              maxWidth: 260,
              maxHeight: "60vh",
              overflowY: "auto",
              overscrollBehavior: "contain",
            }}
          >
            <SubmenuContent
              category={activeCategory!}
              onActivatePrimitive={handleActivatePrimitive}
              onActivateGenerator={handleActivateGenerator}
              onActivateLight={handleActivateLight}
              onActivateMannequin={handleActivateMannequin}
              onActivateLibraryItem={handleActivateLibraryItem}
              isTrial={isTrial}
              libItems={libDisplayItems}
              libFolders={libraryFolders}
              expandedFolders={expandedFolders}
              onToggleFolder={toggleFolder}
              onImportMeshFile={() => fileInputRef.current?.click()}
              onImportBlend={() => blendInputRef.current?.click()}
              referenceLayers={referenceLayers}
              onImagePlaneFromFile={() =>
                imagePlaneFileInputRef.current?.click()
              }
              onImagePlaneFromReference={handleActivateReferenceImage}
            />
          </div>,
          document.body,
        )}

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept={MESH_IMPORT_ACCEPT}
        onChange={handleMeshFileChange}
        style={{ display: "none" }}
      />
      <input
        ref={blendInputRef}
        type="file"
        accept=".blend"
        onChange={handleBlendChange}
        style={{ display: "none" }}
      />
      <input
        ref={imagePlaneFileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImagePlaneFileChange}
        style={{ display: "none" }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Submenu content
// ---------------------------------------------------------------------------

function SubmenuContent({
  category,
  onActivatePrimitive,
  onActivateGenerator,
  onActivateLight,
  onActivateMannequin,
  onActivateLibraryItem,
  isTrial,
  libItems,
  libFolders,
  expandedFolders,
  onToggleFolder,
  onImportMeshFile,
  onImportBlend,
  referenceLayers,
  onImagePlaneFromFile,
  onImagePlaneFromReference,
}: {
  category: CategoryId;
  onActivatePrimitive: (kind: PrimitiveKind) => void;
  onActivateGenerator: (generatorId: string) => void;
  onActivateLight: (kind: "point" | "directional") => void;
  onActivateMannequin: (id: string) => void;
  onActivateLibraryItem: (item: LibraryItemResponse) => void;
  isTrial: boolean;
  libItems: LibraryItemResponse[];
  libFolders: FolderResponse[];
  expandedFolders: Set<string>;
  onToggleFolder: (id: string) => void;
  onImportMeshFile: () => void;
  onImportBlend: () => void;
  referenceLayers: readonly import("../app/storeTypes.ts").ReferenceLayer[];
  onImagePlaneFromFile: () => void;
  onImagePlaneFromReference: (
    imageKey: string,
    name: string,
    imageUrl: string,
  ) => void;
}) {
  switch (category) {
    case "mesh":
      return (
        <>
          {MESH_ITEMS.map((item) => (
            <MenuButtonRow
              key={item.kind}
              data-submenu-item
              onClick={() => onActivatePrimitive(item.kind)}
            >
              <span style={{ color: COLOR_TEXT_DIM, display: "flex" }}>
                {item.icon}
              </span>
              <span>{item.label}</span>
            </MenuButtonRow>
          ))}
        </>
      );

    case "environments":
      return (
        <>
          {ENVIRONMENT_GENERATORS.map((item) => (
            <MenuButtonRow
              key={item.id}
              data-submenu-item
              onClick={() => onActivateGenerator(item.id)}
            >
              <span style={{ color: COLOR_TEXT_DIM, display: "flex" }}>
                {item.icon}
              </span>
              <span>{item.label}</span>
            </MenuButtonRow>
          ))}
        </>
      );

    case "props":
      return (
        <>
          {PROP_GENERATORS.map((item) => (
            <MenuButtonRow
              key={item.id}
              data-submenu-item
              onClick={() => onActivateGenerator(item.id)}
            >
              <span style={{ color: COLOR_TEXT_DIM, display: "flex" }}>
                {item.icon}
              </span>
              <span>{item.label}</span>
            </MenuButtonRow>
          ))}
        </>
      );

    case "light":
      return (
        <>
          {LIGHT_ITEMS.map((item) => (
            <MenuButtonRow
              key={item.kind}
              data-submenu-item
              onClick={() => onActivateLight(item.kind)}
            >
              <span style={{ color: COLOR_TEXT_DIM, display: "flex" }}>
                {item.icon}
              </span>
              <span>{item.label}</span>
            </MenuButtonRow>
          ))}
        </>
      );

    case "mannequin":
      return (
        <>
          {MANNEQUIN_ITEMS.map((item) => {
            const locked = isTrial && item.id !== "male-neutral";
            return (
              <MenuButtonRow
                key={item.id}
                data-submenu-item
                disabled={locked}
                onClick={
                  locked ? undefined : () => onActivateMannequin(item.id)
                }
                title={
                  locked ? "Subscribe to access more body types" : undefined
                }
                style={locked ? { opacity: 0.45 } : undefined}
              >
                <span style={{ color: COLOR_TEXT_DIM, display: "flex" }}>
                  {item.icon}
                </span>
                <span style={{ flex: 1 }}>{item.label}</span>
                {locked && (
                  <span
                    style={{
                      fontSize: FONT_SIZE_TINY,
                      color: COLOR_GOLD,
                      fontWeight: 600,
                    }}
                  >
                    Pro
                  </span>
                )}
              </MenuButtonRow>
            );
          })}
        </>
      );

    case "image_plane": {
      const usableLayers = referenceLayers.filter((l) => !!l.imageUrl);
      return (
        <>
          <MenuButtonRow data-submenu-item onClick={onImagePlaneFromFile}>
            <span style={{ color: COLOR_TEXT_DIM, display: "flex" }}>
              <FileUp size={15} />
            </span>
            <span>From File…</span>
          </MenuButtonRow>
          {usableLayers.length > 0 && (
            <div
              style={{
                height: 1,
                background: "rgba(255,255,255,0.06)",
                margin: "4px 6px",
              }}
            />
          )}
          {usableLayers.length > 0 && (
            <div
              style={{
                fontSize: FONT_SIZE_TINY,
                color: COLOR_TEXT_FAINT,
                padding: "4px 10px",
              }}
            >
              From Reference Panel
            </div>
          )}
          {usableLayers.map((layer) => (
            <MenuButtonRow
              key={layer.id}
              data-submenu-item
              onClick={() =>
                onImagePlaneFromReference(
                  layer.imageKey,
                  layer.name,
                  layer.imageUrl,
                )
              }
            >
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 4,
                  background: "rgba(0,0,0,0.3)",
                  flexShrink: 0,
                  overflow: "hidden",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  src={layer.imageUrl}
                  alt=""
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                  draggable={false}
                />
              </div>
              <span
                style={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {layer.name}
              </span>
            </MenuButtonRow>
          ))}
          {usableLayers.length === 0 && (
            <div
              style={{
                padding: "12px 10px",
                textAlign: "center",
                color: COLOR_TEXT_FAINT,
                fontSize: FONT_SIZE_SMALL,
                lineHeight: 1.5,
              }}
            >
              No reference images yet.
              <br />
              Add images from the Reference panel.
            </div>
          )}
        </>
      );
    }

    case "library": {
      if (libItems.length === 0) {
        return (
          <div
            style={{
              padding: "20px 14px",
              textAlign: "center",
              color: COLOR_TEXT_FAINT,
              fontSize: FONT_SIZE_SMALL,
              lineHeight: 1.5,
            }}
          >
            No objects saved yet.
            <br />
            Select objects and save them to your library.
          </div>
        );
      }

      const byFolder = new Map<string | null, LibraryItemResponse[]>();
      for (const item of libItems) {
        const key = item.folderId ?? null;
        const list = byFolder.get(key) ?? [];
        list.push(item);
        byFolder.set(key, list);
      }

      return (
        <>
          {libFolders.map((folder) => {
            const items = byFolder.get(folder.id);
            if (!items?.length) return null;
            const expanded = expandedFolders.has(folder.id);
            return (
              <div key={folder.id}>
                <MenuButtonRow
                  onClick={() => onToggleFolder(folder.id)}
                  style={{
                    gap: 6,
                    color: COLOR_TEXT_DIM,
                    fontSize: FONT_SIZE_SMALL,
                    padding: "5px 10px",
                  }}
                >
                  <ChevronRight
                    size={11}
                    style={{
                      color: COLOR_TEXT_FAINT,
                      flexShrink: 0,
                      transform: expanded ? "rotate(90deg)" : "none",
                      transition: "transform 0.1s",
                    }}
                  />
                  <Folder
                    size={13}
                    style={{ color: COLOR_ACCENT, flexShrink: 0 }}
                  />
                  <span
                    style={{
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {folder.name}
                  </span>
                  <span
                    style={{
                      fontSize: FONT_SIZE_TINY,
                      color: COLOR_TEXT_FAINT,
                    }}
                  >
                    {items.length}
                  </span>
                </MenuButtonRow>
                {expanded &&
                  items.map((item) => (
                    <LibraryRow
                      key={item.id}
                      item={item}
                      indented
                      onActivate={() => onActivateLibraryItem(item)}
                    />
                  ))}
              </div>
            );
          })}
          {(() => {
            const unfiled = byFolder.get(null);
            if (!unfiled?.length) return null;
            const hasFolderContent = libFolders.some(
              (f) => byFolder.get(f.id)?.length,
            );
            return (
              <>
                {hasFolderContent && (
                  <div
                    style={{
                      height: 1,
                      background: "rgba(255,255,255,0.06)",
                      margin: "4px 6px",
                    }}
                  />
                )}
                {unfiled.map((item) => (
                  <LibraryRow
                    key={item.id}
                    item={item}
                    indented={false}
                    onActivate={() => onActivateLibraryItem(item)}
                  />
                ))}
              </>
            );
          })()}
        </>
      );
    }

    case "import":
      return (
        <>
          <MenuButtonRow data-submenu-item onClick={onImportMeshFile}>
            <span style={{ color: COLOR_TEXT_DIM, display: "flex" }}>
              <FileUp size={15} />
            </span>
            <span>{`Import Mesh File (${MESH_IMPORT_DISPLAY})…`}</span>
          </MenuButtonRow>
          <MenuButtonRow data-submenu-item onClick={onImportBlend}>
            <span style={{ color: COLOR_TEXT_DIM, display: "flex" }}>
              <FileUp size={15} />
            </span>
            <span>Add Blend File…</span>
          </MenuButtonRow>
        </>
      );

    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Library item row
// ---------------------------------------------------------------------------

function LibraryRow({
  item,
  indented,
  onActivate,
}: {
  item: LibraryItemResponse;
  indented: boolean;
  onActivate: () => void;
}) {
  const badge = TYPE_BADGES[item.type];

  return (
    <MenuButtonRow
      data-submenu-item
      onClick={onActivate}
      style={{ paddingLeft: indented ? 32 : 10 }}
    >
      {/* Thumbnail */}
      <div
        style={{
          width: 26,
          height: 26,
          borderRadius: 6,
          background: "rgba(0,0,0,0.3)",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        {item.thumbnailUrl ? (
          <img
            src={item.thumbnailUrl}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            draggable={false}
          />
        ) : item.type === "collection" ? (
          <Layers size={13} style={{ color: COLOR_TEXT_FAINT }} />
        ) : item.type === "blend" ? (
          <FileBox size={13} style={{ color: COLOR_TEXT_FAINT }} />
        ) : (
          <Box size={13} style={{ color: COLOR_TEXT_FAINT }} />
        )}
      </div>

      {/* Name */}
      <span
        style={{
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {item.name}
      </span>

      {/* Type badge */}
      {badge && (
        <span
          style={{
            fontSize: FONT_SIZE_TINY,
            color: badge.color,
            fontWeight: 500,
            flexShrink: 0,
          }}
        >
          [{badge.label}]
        </span>
      )}
    </MenuButtonRow>
  );
}
